/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import NoMoreRegister from '../../component/NoMoreRegister';
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import TextFieldSelectBox, {
  SLIUAddressSelectBox,
} from '../../component/SelectBox';
import SLIFUnitTypeSelectBox from './SLIFUnitTypeSelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
import {
  PrimaryButton,
  AddButton,
  SecondaryButton,
} from '../../component/BasicButton';
//API import
import {
  getCompositionsSLUnit,
  getCompositionsSLIFUnit,
  postCompositionsSLIFUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {sortByUnicode} from '../../functions/sort';
import {format, limit, pullDownMenu} from '../../define';

/**
 * その他空調機追加ダイアログ
 * @param props props
 * @returns JSX
 */
const OtherAddDialog = (props: {
  /** その他空調機追加ボタンの名前 */
  addButtonName: string;
  /** その他空調機の情報 */
  slOtherInfo: apiIF.res.getCompositionsSLIFUnit | null;
  /** その他空調機の情報（set関数） */
  setSLOtherInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsSLIFUnit | null>
  >;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
  /** SL新旧設定 */
  slType: 'new' | 'old';
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID, gwID, systemType: slType} = useParams();

  //SLアドレスの設定項目
  const slOtherAddressCurrencies = [];
  for (let i = 0; i < limit.REGISTRATION_NUM_NEW_SL_IU_MAX; i++) {
    slOtherAddressCurrencies.push({value: String(i), label: String(i)});
  }

  const [otherName, setOtherName] = useState(''); //空調機の名前
  //prettier-ignore
  const [otherAddress, setOtherAddress] = useState(slOtherAddressCurrencies[0]['value']); //SLアドレス
  //prettier-ignore
  const [unitType, setUnitType] = useState<apiIF.reqParameters.postCompositionsSLIFUnit['unit-type']>('FDT'); //空調機タイプ
  //prettier-ignore
  const [ifType, setIFType] = useState<apiIF.reqParameters.postCompositionsSLIFUnit['interface-type']>('SC-ADNA'); //インターフェイスタイプ
  //prettier-ignore
  const [otherPowerSupply, setOtherPowerSupply] = useState<apiIF.reqParameters.postCompositionsSLIFUnit['power-supply']>('threePhase'); //電源の仕様
  const [otherVoltage, setOtherVoltage] = useState('0'); //入力電圧
  const [isOpenDialog, setIsOpenDialog] = useState(false); //ダイアログ表示フラグ（追加ダイアログ・登録超過ダイアログ）
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [unitNumber, setUnitNumber] = useState(0); //空調機台数
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [otherNameError, setOtherNameError] = useState(false); //空調機の名前
  const [otherVoltageError, setOtherVoltageError] = useState(false); //入力電圧

  //入力項目のチェック
  useEffect(() => {
    if (otherName === '' || otherNameError || apiWait || otherVoltageError) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [otherName, otherNameError, otherVoltageError, apiWait]);

  //登録台数を算出
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined || gwID === undefined || slType === undefined) {
      return;
    }

    const promises = [];
    //室内外機の情報を取得
    promises.push(
      getCompositionsSLUnit(siteID, gwID, {
        'fields': 'sl-system,indoor-unit-informations',
        'max-result': 400,
      })
    );
    //その他の空調機の情報を取得
    promises.push(
      getCompositionsSLIFUnit(siteID, gwID, {
        'fields': 'sl-system',
        'max-result': 400,
      })
    );

    Promise.all(promises).then(response => {
      const slInfo = response[0] as apiIF.res.getCompositionsSLUnit;
      const slIFInfo = response[1] as apiIF.res.getCompositionsSLIFUnit;

      //表示している室内機と同じ系統番号のみカウントする
      let num = 0;
      for (const ouInfo of slInfo['outdoor-unit-informations']) {
        if (
          ouInfo['sl-system'] === slType.slice(2) &&
          ouInfo['indoor-unit-informations'] !== undefined
        ) {
          num += ouInfo['indoor-unit-informations'].length;
        }
      }
      for (const uintInfo of slIFInfo['unit-informations']) {
        if (uintInfo['sl-system'] === slType.slice(2)) {
          num++;
        }
      }
      setUnitNumber(num);
    });
  }, [props.slOtherInfo, siteID, gwID, slType]);

  /**
   * ADD I/Fボタン押下時処理
   */
  const handleClickOpen = () => {
    setIsOpenDialog(true);
  };

  /**
   * OKボタン押下時処理
   */
  const addUnit = () => {
    //パスパラメータを取得できない場合
    //空調機を取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      (slType !== 'sl1' && slType !== 'sl2')
    ) {
      return;
    }

    setAPIWait(true);

    //空調機を追加
    const addData: apiIF.reqParameters.postCompositionsSLIFUnit = {
      'unit-name': otherName,
      'sl-address': otherAddress,
      'sl-system': slType.slice(2) as '1' | '2',
      'unit-type': unitType,
      'interface-type': ifType,
      'power-supply': otherPowerSupply,
      'input-voltage': otherVoltage,
    };
    postCompositionsSLIFUnit(siteID, gwID, addData)
      .then(() => {
        //その他の空調機の情報を取得
        return getCompositionsSLIFUnit(siteID, gwID, {'max-result': 400});
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['unit-informations'].sort(sortByUnicode('unit-name'));
        props.setSLOtherInfo(response);
        props.setHasConfigGW(props.isBilling); //課金している場合のみ表示
        handleClose();
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setIsOpenDialog(false);
    setOtherName('');
    setUnitType('FDT');
    setIFType('SC-ADNA');
    setOtherPowerSupply('threePhase');
    setOtherVoltage('0');
  };

  return (
    <>
      {/* その他室内機追加ボタン */}
      <AddButton
        onClick={handleClickOpen}
        id="mv-btnAddIF"
        buttonText={props.addButtonName}
      />
      {/* 登録台数が上限に達している場合は、追加付加のメッセージを表示 */}
      {(props.slType === 'new' &&
        unitNumber >= limit.REGISTRATION_NUM_NEW_SL_IU_MAX) ||
      (props.slType === 'old' &&
        unitNumber >= limit.REGISTRATION_NUM_OLD_SL_IU_MAX) ? (
        <NoMoreRegister
          open={isOpenDialog}
          setOpen={setIsOpenDialog}
          title={trans('NoMoreRegist.errorDlg.title')}
          text={trans('NoMoreRegist.errorDlg.msg')}
        />
      ) : (
        <>
          <Dialog
            open={isOpenDialog}
            onClose={handleClose}
            aria-labelledby="unit-add-dialog-title">
            <DialogTitle id="unit-add-dialog-title">
              {trans('OtherAddDialog.dialogBox.title')}
            </DialogTitle>
            <DialogContent>
              {/* ダイアログ内に表示するメッセージ */}
              <DialogContentText>
                {trans('OtherAddDialog.dialogBox.msg')}
              </DialogContentText>
              <Stack spacing={1.5}>
                {/* 室内機名称の入力 */}
                <ChkInputTextbox
                  setValue={setOtherName}
                  setError={setOtherNameError}
                  chkLength={format.BASIC_LENGTH_MAX}
                  type="text"
                  label={trans(
                    'OtherAddDialog.dialogBox.txtField.label.interfaceName'
                  )}
                  value={otherName}
                  id="mv-inputIFName"
                  class="gwSetting__contents__textBox--unitName"
                  helperText=" "
                  errHelperText={trans(
                    'OtherAddDialog.dialogBox.txtField.errHelperTxt'
                  )}
                  variant="standard"
                  autoFocus={true}
                  margin="dense"
                  chkInputProhibitedCharacter={format.BASIC_PATTERN_S3_POLICY}
                />
                {/* SLアドレスの選択 */}
                <SLIUAddressSelectBox
                  setAddress={setOtherAddress}
                  label={trans(
                    'OtherAddDialog.dialogBox.txtField.label.superlinkAddress'
                  )}
                  class="gwSetting__contents__textBox--address"
                  id="mv-selectIFUnit"
                />
                {/* 空調機タイプ */}
                <SLIFUnitTypeSelectBox setUnitType={setUnitType} />
                {/* インターフェイスタイプの選択 */}
                <TextFieldSelectBox
                  label={trans(
                    'OtherAddDialog.dialogBox.txtField.label.interfaceType'
                  )}
                  choices={pullDownMenu.INTERFACE_TYPE}
                  setValue={setIFType}
                  default={ifType}
                  id="mv-otherAddIntfTypeSelect"
                  class="gwSetting__contents__selectBox--interface"
                />
                {/* 電圧の入力 */}
                <ChkInputTextbox
                  setValue={setOtherVoltage}
                  setError={setOtherVoltageError}
                  chkInputTxtOK={format.VOLTAGE_PATTERN_OK}
                  type="text"
                  label={trans(
                    'OtherAddDialog.dialogBox.txtField.label.voltage'
                  )}
                  value={otherVoltage}
                  id="mv-otherAddVoltageInput"
                  class="gwSetting__contents__textBox--voltage"
                  helperText=" "
                  errHelperText={trans(
                    'OtherAddDialog.dialogBox.txtField.errHelperTxt'
                  )}
                  variant="standard"
                />
                {/* 電源の種類 */}
                <TextFieldSelectBox
                  label={trans(
                    'OtherAddDialog.dialogBox.txtField.label.powerSupply'
                  )}
                  choices={pullDownMenu.POWER_SUPPLY_TYPE}
                  setValue={setOtherPowerSupply}
                  default={otherPowerSupply}
                  id="mv-otherAddPowSplySelect"
                  class="gwSetting__contents__selectBox--powerSupply"
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <SecondaryButton
                disabled={apiWait}
                onClick={handleClose}
                buttonText={trans('OtherAddDialog.dialogBox.buttonTxt.cancel')}
                id="mv-otherAddCancelBtn"
              />
              <PrimaryButton
                onClick={addUnit}
                buttonText={trans('OtherAddDialog.dialogBox.buttonTxt.ok')}
                id="mv-otherAddOkBtn"
                disabled={okBtnDisabled}
                apiWait={apiWait}
              />
            </DialogActions>
          </Dialog>
          <APIErrorMessage
            open={isOpenErrorDialog}
            setOpen={setIsOpenErrorDialog}
            id="mv-otherAddErrDlgBtn"
          />
        </>
      )}
    </>
  );
};
export default OtherAddDialog;
